/** DM 1:1 entre dois usuários humanos (não agente/chatbot). */
export function isUserToUserTeamDm(space) {
  if (space?.type !== "dm") return false;
  const peerType = space?.dmPeerType;
  return peerType !== "agent" && peerType !== "chatbot";
}

/** Master em DM entre usuários: não pode enviar nem interagir (conversa privada 1:1). */
export function isMasterTeamDmReadOnly(userData, space) {
  return Number(userData?.master) === 1 && isUserToUserTeamDm(space);
}

/** Mesmo breakpoint do chat (768px) para layout lista/thread. */
export const TEAM_MOBILE_MEDIA_QUERY = "(max-width: 768px)";

/** Altura compacta do composer em mobile (evita área excessiva). */
export const TEAM_MOBILE_COMPOSER_ROW_HEIGHT_PX = 48;

/** Botões da barra inferior mobile — alvo de toque confortável. */
export const TEAM_MOBILE_RAIL_BUTTON_SIZE_PX = 46;

/** Ícones da barra inferior mobile. */
export const TEAM_MOBILE_RAIL_ICON_SIZE_PX = 26;

/** Cabeçalho da thread no mobile — título centralizado verticalmente. */
export const teamMobileThreadHeaderRowSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  px: 1,
  py: 0.75,
  minHeight: 48,
};

export const teamMobileThreadHeaderTitleSx = {
  fontSize: "0.9375rem",
  lineHeight: 1.25,
  fontWeight: 700,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

/** ID do space aberto a partir da rota /equipe/:id ou /popup-equipe/:id. */
export function parseTeamSpaceIdFromPathname(pathname) {
  const m = String(pathname || "").match(/\/(?:popup-)?equipe\/(\d+)/);
  if (!m) return null;
  const sid = Number(m[1]);
  return Number.isFinite(sid) && sid > 0 ? sid : null;
}

/** Space ativo na UI (workspace /equipe ou console com ?space=). */
export function parseTeamSpaceIdFromLocation(pathname, search = "") {
  const fromPath = parseTeamSpaceIdFromPathname(pathname);
  if (fromPath) return fromPath;
  if (!isTeamConversationRoute(pathname)) return null;
  try {
    const space = Number(new URLSearchParams(String(search || "")).get("space"));
    return Number.isFinite(space) && space > 0 ? space : null;
  } catch {
    return null;
  }
}

/** Usuário está em qualquer tela de conversas internas (workspace ou console). */
export function isTeamConversationRoute(pathname) {
  const path = String(pathname || "");
  if (path === "/equipe" || path.startsWith("/equipe/")) return true;
  if (path === "/popup-equipe" || path.startsWith("/popup-equipe/")) return true;
  if (path === "/platform/xprobase/team" || path.startsWith("/platform/xprobase/team/")) {
    return true;
  }
  if (path === "/partner/console/team" || path.startsWith("/partner/console/team/")) {
    return true;
  }
  return false;
}

/** Estilo dos painéis da equipe — sem bordas no mobile para aproveitar a tela. */
export function getTeamPaneContainerStyle(theme, isMobile) {
  if (isMobile) {
    return {
      border: "none",
      borderRadius: 0,
      boxShadow: "none",
      overflow: "hidden",
    };
  }
  return {
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 1px 4px rgba(0, 0, 0, 0.5)"
        : "0 1px 4px rgba(0, 0, 0, 0.1)",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px",
    overflow: "hidden",
  };
}

/** ID da solicitação vinculada a um canal de discussão interna (slug, nome, mensagens). */
export function resolveTeamSpaceLeadId(space, messages = []) {
  const slug = String(space?.slug || "");
  // Canal de tarefa não é solicitação — o #id na descrição é o cardId.
  if (/^tarefa-\d+$/i.test(slug)) return null;

  const fromMsg = [...(messages || [])].reverse().find((m) => m?.leadId)?.leadId;
  if (fromMsg != null) {
    const n = Number(fromMsg);
    if (Number.isFinite(n) && n > 0) return n;
  }

  const slugMatch = /^solicitacao-(\d+)$/i.exec(slug);
  if (slugMatch) return Number(slugMatch[1]);

  // Fallback nome/descrição só para espaços que não são discussão de tarefa.
  const name = String(space?.name || "");
  const nameMatch = /#\s*(\d+)/.exec(name);
  if (nameMatch) return Number(nameMatch[1]);

  const desc = String(space?.description || "");
  const descMatch = /#\s*(\d+)/.exec(desc);
  if (descMatch) return Number(descMatch[1]);

  return null;
}

/** ID do card de tarefa vinculado a um canal (slug tarefa-{cardId}). */
export function resolveTeamSpaceCardId(space) {
  const fromProp = space?.cardId != null ? Number(space.cardId) : null;
  if (Number.isFinite(fromProp) && fromProp > 0) return fromProp;

  const slugMatch = /^tarefa-(\d+)$/i.exec(String(space?.slug || ""));
  if (slugMatch) {
    const n = Number(slugMatch[1]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/** DM humano já existente com o usuário (peer). */
export function findUserDmSpace(spaces, userId) {
  const uid = Number(userId);
  if (!Number.isFinite(uid) || uid <= 0) return null;
  return (
    (spaces || []).find(
      (s) =>
        s?.type === "dm" &&
        s?.dmPeerType === "user" &&
        Number(s?.dmPeerUserId) === uid
    ) || null
  );
}

export function formatMessageTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

/** Data e hora para histórico de edições e metadados. */
export function formatMessageDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export function formatDayLabel(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
    if (sameDay(d, today)) return "Hoje";
    if (sameDay(d, yesterday)) return "Ontem";
    return d.toLocaleDateString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  } catch {
    return "";
  }
}

export function isSameAuthorGroup(prev, curr) {
  if (!prev || !curr) return false;
  if (prev.authorType !== curr.authorType) return false;
  if (String(prev.authorId) !== String(curr.authorId)) return false;
  const t1 = new Date(prev.createdAt).getTime();
  const t2 = new Date(curr.createdAt).getTime();
  if (Number.isNaN(t1) || Number.isNaN(t2)) return false;
  return Math.abs(t2 - t1) < 5 * 60 * 1000;
}

export function dayKey(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** DM válido para inbox (portal ou equipe). */
export function isValidTeamDmSpace(space) {
  if (!space || typeof space !== "object" || Array.isArray(space)) return false;
  if (String(space.type || "").toLowerCase() !== "dm") return false;
  const id = Number(space.id);
  return Number.isFinite(id) && id > 0;
}

export async function mapWithConcurrency(items, limit, fn) {
  const out = [];
  let i = 0;
  const workers = Array.from({ length: Math.min(limit, items.length || 1) }, async () => {
    while (i < items.length) {
      const idx = i;
      i += 1;
      try {
        // eslint-disable-next-line no-await-in-loop
        const chunk = await fn(items[idx], idx);
        if (Array.isArray(chunk)) {
          if (chunk.length) out.push(...chunk);
        } else if (chunk) {
          out.push(chunk);
        }
      } catch {
        /* ignore item */
      }
    }
  });
  await Promise.all(workers);
  return out;
}
